import React, { useEffect, useMemo, useState } from 'react';
import { ResponsiveContainer, BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';
import { Card, CardContent, CardHeader } from '../ui/card';
import { ChartContainer, ChartTooltipContent } from '../ui/chart';
import SegmentedControl from './SegmentedControl';
import GPSWaterfallChart from './GPSWaterfallChart';
import { useGPSEvolutionData } from '../../hooks/useGPSEvolutionData';

const chartConfig = {
  created: { label: 'Reçus', color: 'rgb(47 109 240)' },
  resolved: { label: 'Fermés', color: 'rgb(22 168 102)' },
  active: { label: 'Tickets en cours', color: 'rgb(58 74 99)' },
};

// Allowed tabs per "Afficher" sense.
const TABS_BY_SENSE = {
  all: [
    { key: 'evolution', label: 'Évolution' },
    { key: 'ongoing', label: 'En cours' },
    { key: 'bridge', label: "Pont d'inventaire" },
  ],
  received: [{ key: 'evolution', label: 'Reçus' }],
  resolved: [{ key: 'evolution', label: 'Fermés' }],
  ongoing: [
    { key: 'ongoing', label: 'En cours' },
    { key: 'bridge', label: "Pont d'inventaire" },
  ],
};

const SUBTITLES = {
  evolution: {
    all: 'Flux — tickets reçus et fermés par période.',
    received: 'Flux — tickets reçus par période.',
    resolved: 'Flux — tickets fermés par période.',
  },
  ongoing: { all: 'Encours — évolution du backlog ouvert dans le temps.', ongoing: 'Encours — évolution du backlog ouvert dans le temps.' },
  bridge: { all: "Encours — d'où vient le backlog : ouverts début + reçus − fermés.", ongoing: "Encours — d'où vient le backlog : ouverts début + reçus − fermés." },
};

const GPSEvolutionCard = ({ stats, sense = 'all' }) => {
  const { chartData, activeChartData } = useGPSEvolutionData(stats);
  const tabs = TABS_BY_SENSE[sense] || TABS_BY_SENSE.all;
  const [tab, setTab] = useState(tabs[0].key);

  // Resnap to the first allowed tab whenever the sense (allowed set) changes.
  useEffect(() => {
    if (!tabs.some((t) => t.key === tab)) setTab(tabs[0].key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sense]);

  const showCreated = sense === 'all' || sense === 'received';
  const showResolved = sense === 'all' || sense === 'resolved';

  const bridge = useMemo(() => {
    const received = stats?.openTickets?.length || 0;
    const resolved = stats?.closedTickets?.length || 0;
    const end = stats?.ongoingTickets?.length || 0;
    const start = Math.max(0, end - received + resolved);
    return { start, received, resolved, end };
  }, [stats]);

  const subtitle = (SUBTITLES[tab] && (SUBTITLES[tab][sense] || SUBTITLES[tab].all)) || '';
  const evolutionEmpty = tab === 'evolution' && !chartData.length;

  return (
    <Card className="h-full">
      <CardHeader className="!p-4 !pb-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 sm:gap-2.5">
            <span className="inline-flex items-center justify-center rounded-lg w-9 h-9 bg-[#f3f1fe] dark:bg-[#2a2440] text-[#5b3df0] dark:text-[#a98ff5]">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 3v18h18" /><rect x="7" y="10" width="3" height="8" /><rect x="13" y="6" width="3" height="12" />
              </svg>
            </span>
            <span className="text-sm sm:text-base font-semibold text-gray-900 dark:text-white">Évolution des tickets</span>
          </div>
          {tabs.length > 1 && (
            <SegmentedControl
              size="s"
              className="!w-auto"
              value={tab}
              onChange={setTab}
              options={tabs.map((t) => ({ value: t.key, label: t.label }))}
            />
          )}
        </div>
        {subtitle && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 text-left">{subtitle}</p>}
      </CardHeader>
      <CardContent className="!px-4 !pb-4">
        {tab === 'bridge' ? (
          <GPSWaterfallChart {...bridge} />
        ) : evolutionEmpty ? (
          <p className="text-gray-600 dark:text-gray-400 text-center py-16">
            Aucune donnée d'évolution disponible pour la période sélectionnée.
          </p>
        ) : (
          <ChartContainer config={chartConfig} className="h-80 w-full">
            <ResponsiveContainer width="100%" height="100%">
              {tab === 'evolution' ? (
                <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} style={{ fontSize: '12px' }} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} tickMargin={8} style={{ fontSize: '12px' }} />
                  <Tooltip content={<ChartTooltipContent indicator="dashed" />} />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                  {showCreated && <Bar name="Reçus" dataKey="created" fill="#2f6df0" radius={[4, 4, 0, 0]} />}
                  {showResolved && <Bar name="Fermés" dataKey="resolved" fill="#16a866" radius={[4, 4, 0, 0]} />}
                </BarChart>
              ) : (
                <AreaChart data={activeChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="gpsBacklogArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3a4a63" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="#3a4a63" stopOpacity={0.04} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={8} style={{ fontSize: '12px' }} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} tickMargin={8} style={{ fontSize: '12px' }} />
                  <Tooltip content={<ChartTooltipContent />} />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="circle" />
                  <Area type="monotone" name="Backlog ouvert" dataKey="active" stroke="#3a4a63" strokeWidth={2} fillOpacity={1} fill="url(#gpsBacklogArea)" />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </ChartContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default GPSEvolutionCard;
